/**
 * EntityPage — one component for /people/:slug, /organizations/:slug, /operations/:slug.
 *
 * Renders a compact intelligence-reference page (NOT a biography):
 *   • Hero banner (kind + name + cameo count + active years)
 *   • "Appears In" — every dossier where this entity is a key player
 *   • "Frequently Connected" — top co-appearing entities (mixed kinds)
 *   • Aliases chip row when > 1 form of the name has been observed
 *
 * The entity kind derives from the URL prefix. If the API returns 404
 * (no such slug for that kind) we render a graceful "entity not found"
 * state that still surfaces the archive search.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowUpRight, ShieldCheck, User, Building2, Crosshair, Compass, Clock, AlertCircle,
} from "lucide-react";
import { EnvironmentalSeal } from "../components/EnvironmentalSeal";
// NetworkStrength import removed — panel retired (duplicated identity stats)
import { EntityProfile } from "../components/EntityProfile";
import { setInvestigationTitle } from "../investigation/investigationMemory";
import { InvestigationContinues } from "../investigation/InvestigationContinues";
import { LearningPanels } from "../components/learning/LearningPanels";
import { NewToThisTopic } from "../components/learning/NewToThisTopic";
import { EntityHoverCard } from "../components/learning/EntityHoverCard";
import { RecordPortrait, RecordMediaGallery } from "../components/media/PublishedMedia";
import { PinToWallButton } from "../workbench/PinToWallButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
  person:       { icon: User,       label: "Person",           color: "#D92D20" },
  organization: { icon: Building2,  label: "Organization",     color: "#A68F5E" },
  operation:    { icon: Crosshair,  label: "Operation",        color: "#3FB950" },
};

const FINE_KIND_LABEL = {
  government_agency: "Government Agency", company: "Company",
  group_or_network: "Group / Network", law_or_policy: "Law / Policy",
  court_case: "Court Case", program: "Program", event: "Event",
  place: "Place", document: "Document", person: "Person",
  organization: "Organization", operation: "Operation",
};

const GROUP_OF_KIND = (k) => {
  if (k === "person") return "person";
  if (k === "operation" || k === "program") return "operation";
  return "organization";
};

const EntityPage = ({ kind }) => {
  const { slug } = useParams();
  const [entity, setEntity] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound | error

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setStatus("loading");
      try {
        const { data } = await axios.get(`${API}/entities/${kind}/${slug}`);
        if (!cancelled) { setEntity(data); setStatus("ok"); }
      } catch (err) {
        if (cancelled) return;
        setStatus(err?.response?.status === 404 ? "notfound" : "error");
      }
    })();
    return () => { cancelled = true; };
  }, [kind, slug]);

  useEffect(() => {
    if (entity?.name) setInvestigationTitle(entity.name);
  }, [entity?.name]);

  const meta = KIND_META[kind] || KIND_META.person;
  const Icon = meta.icon;

  const grouped = useMemo(() => {
    const rel = entity?.related_entities || [];
    return {
      person:       rel.filter((r) => GROUP_OF_KIND(r.kind) === "person").slice(0, 12),
      organization: rel.filter((r) => GROUP_OF_KIND(r.kind) === "organization").slice(0, 12),
      operation:    rel.filter((r) => GROUP_OF_KIND(r.kind) === "operation").slice(0, 12),
    };
  }, [entity]);

  const profileYears = entity?.profile?.active_range || null;

  // Type-aware classification: prefer the profile's own documented
  // "type" identity fact over the coarse graph kind label.
  const classification = useMemo(() => {
    const facts = entity?.profile?.identity_facts || [];
    const typed = facts.find((f) =>
      /^(organization type|type|entity type|classification)$/i.test((f.label || "").trim()));
    return typed?.value || FINE_KIND_LABEL[entity?.kind] || null;
  }, [entity]);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (status === "loading") {
    return (
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-24" data-testid="entity-loading">
        <div className="animate-pulse h-8 w-64 bg-[#2D2A26] mb-4" />
        <div className="animate-pulse h-14 w-96 bg-[#2D2A26]" />
      </div>
    );
  }

  if (status === "notfound") {
    return (
      <div
        data-testid="entity-not-found"
        className="max-w-3xl mx-auto px-4 md:px-8 py-24 text-center"
      >
        <AlertCircle size={28} className="text-[#D92D20] mx-auto mb-4" />
        <div className="font-display text-3xl md:text-4xl text-[#E4DFD3] mb-2">
          Entity not on file
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#7B7168] mb-8">
          // {kind.toUpperCase()} / {slug}
        </div>
        <Link
          to="/archive"
          data-testid="entity-back-to-archive"
          className="inline-flex items-center gap-2 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.35em] border border-[#D92D20] text-[#E4DFD3] hover:bg-[#D92D20] transition-colors"
        >
          Return to Archive
        </Link>
      </div>
    );
  }

  if (!entity) return null;

  const cameos = entity.appears_in || [];

  return (
    <div data-testid="entity-page" style={{ background: "#0B0A0D" }} className="min-h-[70vh]">
      {/* Hero */}
      <section
        data-testid="entity-hero"
        className="relative overflow-hidden border-b"
        style={{ borderColor: "#2D2A26" }}
      >
        {/* Locked branding — topic pages run the seal at 25 % intensity. */}
        <EnvironmentalSeal intensity="topic" />
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(228,223,211,1) 1px, transparent 1px),linear-gradient(90deg, rgba(228,223,211,1) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10"
          style={{ background: `radial-gradient(ellipse 90% 60% at 50% 0%, ${meta.color}22 0%, transparent 55%)` }}
        />
        <div className="relative max-w-6xl mx-auto px-4 md:px-8 pt-14 pb-10 md:pt-20 md:pb-14">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10" style={{ background: meta.color }} />
            <Icon size={14} style={{ color: meta.color }} />
            <span className="font-mono text-[10px] uppercase tracking-[0.5em] text-[#A39E93]">
              Intelligence Reference · {meta.label}
            </span>
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:items-start sm:justify-between gap-6">
            <div className="min-w-0">
              <h1
                data-testid="entity-name"
                className="font-display text-[#E4DFD3] leading-[0.98] tracking-tight
                           text-[36px] sm:text-[48px] md:text-[64px]"
              >
                {entity.name}
              </h1>
              <div className="mt-3">
                <PinToWallButton type="entity" refId={entity.slug} />
              </div>
            </div>
            <RecordPortrait targetId={entity.slug} name={entity.name} kind={entity.kind} />
          </div>
          {/* Aliases (only if more than one form observed) */}
          {(entity.aliases || []).length > 1 && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#7B7168]">
                Also referenced as:
              </span>
              {entity.aliases.filter((a) => a !== entity.name).slice(0, 6).map((a) => (
                <span key={a} className="font-mono text-[11px] text-[#B9B2A4] border border-[#3A3630] px-2 py-1">{a}</span>
              ))}
            </div>
          )}
          {/* Archive Summary — who is this, why are they here */}
          {entity.archive_summary?.text && (
            <div className="mt-6 max-w-3xl border-l-2 pl-4 md:pl-5" style={{ borderColor: "#D92D20" }}>
              <div className="font-mono text-[9px] uppercase tracking-[0.4em] text-[#D92D20] mb-2">
                Archive Summary
              </div>
              <p className="text-[14px] md:text-[15px] leading-relaxed text-[#CFC9BC]" data-testid="entity-archive-summary">
                {entity.archive_summary.text}
              </p>
            </div>
          )}

          {/* Compact archive stats — shown once, clickable navigation */}
          <div className="mt-6 flex flex-wrap items-center gap-x-2 gap-y-2 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-[#8A8377]" data-testid="entity-compact-stats">
            <button
              type="button"
              onClick={() => scrollTo("entity-referenced-in")}
              data-testid="stat-dossier-appearances"
              className="inline-flex items-center gap-1 text-[#E4DFD3] hover:text-[#D92D20] transition-colors border-b border-dotted border-[#454038] pb-0.5"
            >
              {entity.cameo_count} dossier{entity.cameo_count === 1 ? "" : "s"} <ArrowUpRight size={9} />
            </button>
            <span className="text-[#454038]">·</span>
            <button
              type="button"
              onClick={() => scrollTo("entity-related-network")}
              data-testid="stat-network-connections"
              className="inline-flex items-center gap-1 text-[#E4DFD3] hover:text-[#D92D20] transition-colors border-b border-dotted border-[#454038] pb-0.5"
            >
              {(entity.related_entities || []).length} connections <ArrowUpRight size={9} />
            </button>
            {(profileYears || entity.first_year) && (
              <>
                <span className="text-[#454038]">·</span>
                <span>{profileYears || `${entity.first_year} — ${entity.last_year}`}</span>
              </>
            )}
            {(classification || meta.label) && (
              <>
                <span className="text-[#454038]">·</span>
                <span className="text-[#A68F5E]">{classification || meta.label}</span>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Context Layer — beginner panels only when no full Archive Profile
          exists (a profile carries its own summary + Archive relevance,
          so rendering both would repeat the same information twice). */}
      {!entity.profile && <LearningPanels entity={entity} />}

      {/* Authenticated imagery on file for this entity */}
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <RecordMediaGallery targetId={entity.slug} heading="AUTHENTICATED IMAGERY"
                            skipRoles={["portrait", "primary"]} />
      </div>

      {/* Archive Profile (Entity Research Standard) */}
      {entity.profile && <EntityProfile profile={entity.profile} />}

      {/* Network Strength — why this entity matters inside The Archive */}
      {/* NetworkStrength removed — its numbers duplicated the identity
          snapshot; the network is now explained by the rails below. */}

      {/* Appears in */}
      <section id="entity-referenced-in" className="max-w-6xl mx-auto px-4 md:px-8 py-14 md:py-16 scroll-mt-24">
        <div className="flex items-center gap-3 mb-6 md:mb-8">
          <ShieldCheck size={13} className="text-[#D92D20]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.5em] text-[#D92D20]">
            Referenced In
          </span>
          <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#454038]">
            {String(cameos.length).padStart(2, "0")}
          </span>
        </div>
        {cameos.length === 0 ? (
          <div className="font-mono text-[12px] text-[#7B7168] italic">No published dossiers reference this entity yet.</div>
        ) : (
          <ul data-testid="entity-appears-in" className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {cameos.map((c) => (
              <li key={c.slug}>
                <Link
                  to={`/topic/${c.slug}`}
                  data-testid={`entity-appears-in-${c.slug}`}
                  className="group flex items-start gap-3 border p-4 hover:border-[#D92D20] transition-colors"
                  style={{ borderColor: "#2D2A26", background: "#0F0E12" }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] text-[#E4DFD3] font-heading leading-tight group-hover:text-[#D92D20] transition-colors">
                      {c.title || c.slug}
                    </div>
                  </div>
                  <ArrowUpRight size={13} className="text-[#454038] group-hover:text-[#D92D20] shrink-0 mt-0.5 transition-colors" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Frequently Connected */}
      {(entity.related_entities || []).length > 0 && (
        <section id="entity-related-network" className="max-w-6xl mx-auto px-4 md:px-8 pb-16 md:pb-20 space-y-10 md:space-y-12 scroll-mt-24">
          <div className="flex items-center justify-between flex-wrap gap-3 -mb-4 md:-mb-5">
            <div className="flex items-center gap-3">
              <Compass size={13} className="text-[#D92D20]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.5em] text-[#D92D20]">
                Network Connections
              </span>
              <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#454038]">
                {String((entity.related_entities || []).length).padStart(2, "0")}
              </span>
            </div>
            <Link
              to={`/connections/${entity.slug}`}
              data-testid="open-connections-explorer"
              className="inline-flex items-center gap-2 border px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93] hover:text-[#D92D20] hover:border-[#D92D20] transition-colors"
              style={{ borderColor: "#3A3630" }}
            >
              Open in Connections Explorer <ArrowUpRight size={11} />
            </Link>
          </div>
          <RailGroup title="Related People"        icon={User}       items={grouped.person}       basePath="/people" />
          <RailGroup title="Related Organizations" icon={Building2}  items={grouped.organization} basePath="/organizations" />
          <RailGroup title="Related Operations"    icon={Crosshair}  items={grouped.operation}    basePath="/operations" />
        </section>
      )}

      {/* NEW TO THIS TOPIC? — optional starting points on advanced pages */}
      <NewToThisTopic />

      {/* CONTINUE YOUR INVESTIGATION — permanent V2.0 navigation signature */}
      <InvestigationContinues />
    </div>
  );
};

const Stat = ({ label, value, onClick, testid }) => {
  const inner = (
    <>
      <div className="font-display text-[20px] md:text-[26px] text-[#E4DFD3] leading-none group-hover/stat:text-[#D92D20] transition-colors">
        {value ?? "—"}
      </div>
      <div className="font-mono text-[9px] uppercase tracking-[0.35em] text-[#8A8377] mt-1.5 flex items-center gap-1.5">
        {label}
        {onClick && <ArrowUpRight size={9} className="text-[#454038] group-hover/stat:text-[#D92D20] transition-colors" />}
      </div>
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        data-testid={testid}
        className="group/stat text-left border-l-2 pl-3 md:pl-4 cursor-pointer"
        style={{ borderColor: "#D92D20" }}
        title={`View ${label.toLowerCase()}`}
      >
        {inner}
      </button>
    );
  }
  return (
    <div className="border-l-2 pl-3 md:pl-4" style={{ borderColor: "#D92D20" }} data-testid={testid}>
      {inner}
    </div>
  );
};

const RailGroup = ({ title, icon: Icon, items, basePath }) => {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={13} className="text-[#D92D20]" />
        <span className="font-mono text-[10px] uppercase tracking-[0.5em] text-[#D92D20]">{title}</span>
        <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#454038]">
          {String(items.length).padStart(2, "0")}
        </span>
      </div>
      <ul className="flex flex-wrap gap-2">
        {items.map((r) => (
          <li key={r.slug}>
            <EntityHoverCard
              slug={r.slug}
              whyConnected={`Co-documented in ${r.co_count || 1} shared dossier${(r.co_count || 1) === 1 ? "" : "s"}`}
            >
            <Link
              to={`${basePath}/${r.slug}`}
              className="inline-flex items-center gap-2 border px-3 py-1.5 font-mono text-[11px] text-[#E4DFD3] hover:border-[#D92D20] hover:text-[#D92D20] transition-colors"
              style={{ borderColor: "#3A3630", background: "#0F0E12" }}
              data-testid={`entity-related-${r.slug}`}
            >
              {r.name}
              <span className="text-[9px] font-mono text-[#7B7168] tracking-[0.15em]">×{r.co_count}</span>
              <span
                className="font-mono text-[7.5px] uppercase tracking-[0.15em] px-1 py-[1px]"
                style={(r.co_count || 0) >= 2
                  ? { color: "#5FC96C", border: "1px solid #2E7D3A", borderRadius: 2 }
                  : { color: "#D9B44A", border: "1px solid #8A6D1F", borderRadius: 2 }}
                title={`Co-documented in ${r.co_count || 1} shared dossier${(r.co_count || 1) === 1 ? "" : "s"}`}
              >
                {(r.co_count || 0) >= 2 ? "CORROBORATED" : "REPORTED"}
              </span>
            </Link>
            </EntityHoverCard>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default EntityPage;

// Convenience wrappers so <Route element={...}/> stays declarative.
export const PersonPage       = () => <EntityPage kind="person" />;
export const OrganizationPage = () => <EntityPage kind="organization" />;
export const OperationPage    = () => <EntityPage kind="operation" />;
